import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, Tabs } from 'antd'
import PageHeader from '@/components/PageHeader'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { ORDER_TYPE_LIST, ORDER_TYPE_CONFIG } from './config/orderTypeConfig'
import OrderTableFactory from './components/OrderTableFactory'
import './Page.less'

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '订单列表' }
]

// 获取默认查询参数（单个订单类型）
const getDefaultQueryForType = (orderType) => {
  let storeId = ''
  try {
    const { search } = window.location || {}
    if (search) {
      const params = new URLSearchParams(search)
      const storeIdParam = params.get('storeId')
      if (storeIdParam) {
        const parsed = parseInt(storeIdParam, 10)
        if (!Number.isNaN(parsed)) {
          storeId = String(parsed)
        }
      }
    }
  } catch (e) {
    // 容错处理
  }
  return {
    storeId: storeId || '',
    status: '',
    serviceType: '',
    startTime: '',
    endTime: '',
    page: 1,
    pageSize: 20
  }
}

// 从URL获取初始订单类型
const getInitialOrderType = () => {
  try {
    const { search } = window.location || {}
    if (search) {
      const params = new URLSearchParams(search)
      const orderTypeParam = params.get('orderType')
      if (orderTypeParam && ORDER_TYPE_CONFIG[orderTypeParam]) {
        return orderTypeParam
      }
    }
  } catch (e) {
    // 容错处理
  }
  return ORDER_TYPE_LIST[0]?.key || 'maternity_center'
}

class OrderListPage extends React.Component {
  constructor(props) {
    super(props)
    
    // 初始化当前订单类型
    const initialOrderType = getInitialOrderType()
    
    // 为每个订单类型初始化独立的query状态
    const queriesByType = {}
    ORDER_TYPE_LIST.forEach(type => {
      queriesByType[type.key] = getDefaultQueryForType(type.key)
    })
    
    this.state = {
      currentOrderType: initialOrderType,
      queriesByType: queriesByType
    }
  }

  componentDidMount() {
    const { dispatch } = this.props
    
    // 加载门店列表（只需加载一次）
    dispatch(fetchJStoreList({ query: {}, dataType: 'optionErp' }))
      .then((action) => {
        // 门店列表拉取成功后，优先用 URL/storeId，否则用第一个门店 id
        const storeList = action?.payload?.data
        const firstStoreId = Array.isArray(storeList) ? storeList?.[0]?.id : undefined

        const currentQuery = this.getCurrentQuery()
        const storeIdToUse = currentQuery?.storeId || (firstStoreId != null ? String(firstStoreId) : '')

        // 将 storeId 写回当前订单类型的 query（给非月子中心的筛选控件使用）
        if (storeIdToUse && !currentQuery?.storeId) {
          this.updateQuery('storeId', storeIdToUse)
        }
      })
  }

  /**
   * 获取当前订单类型的query
   */
  getCurrentQuery = () => {
    const { currentOrderType, queriesByType } = this.state
    return queriesByType[currentOrderType] || getDefaultQueryForType(currentOrderType)
  }

  /**
   * 更新当前订单类型的query参数
   */
  updateQuery = (keyOrUpdates, value) => {
    const { currentOrderType, queriesByType } = this.state
    const currentQuery = queriesByType[currentOrderType] || getDefaultQueryForType(currentOrderType)
    
    let updates = {}
    if (typeof keyOrUpdates === 'string') {
      updates[keyOrUpdates] = value
    } else if (typeof keyOrUpdates === 'object' && keyOrUpdates !== null) {
      updates = keyOrUpdates
    }
    
    // 检查是否有变化
    let hasChange = false
    for (const key in updates) {
      if (currentQuery[key] !== updates[key]) {
        hasChange = true
        break
      }
    }
    
    if (!hasChange) {
      return currentQuery
    }
    
    // 更新state
    const newQueriesByType = {
      ...queriesByType,
      [currentOrderType]: {
        ...currentQuery,
        ...updates
      }
    }
    
    this.setState({ queriesByType: newQueriesByType }, () => {
      // 更新后重新获取数据
      // this.fetchOrders() // 注释：子组件已自行管理数据获取
    })
    
    return newQueriesByType[currentOrderType]
  }

  /**
   * 获取订单列表数据
   */
  // fetchOrders = async () => { // 注释：子组件已自行管理数据获取
  //   const { dispatch } = this.props
  //   const { currentOrderType } = this.state
  //   const query = this.getCurrentQuery()
  //   
  //   // 调用API获取数据
  //   await dispatch(fetchOrdersByType({ query, orderType: currentOrderType }))
  // }

  /**
   * 分页切换
   */
  handlePageChange = (page, pageSize) => {
    // 更新页码并重新获取数据
    this.updateQuery({ page, pageSize })
  }

  /**
   * 查看订单详情
   */
  handleViewDetail = (orderId) => {
    // 详情通过抽屉组件显示，不需要跳转
    // 抽屉组件会通过 children 的 onClick 触发显示
  }

  /**
   * 处理异常订单成功回调
   */
  handleAbnormalSuccess = () => {
    // 刷新列表
    // this.fetchOrders() // 注释：子组件已自行管理数据获取
  }

  /**
   * 获取订单详情用于处理弹窗
   */
  getOrderDetailForHandle = (orderId) => {
    // 从列表数据中查找订单详情
    const { orderObj } = this.props
    const records = orderObj?.orderListPage?.data?.records ?? []
    return records.find(item => String(item.id) === String(orderId))
  }


  /**
   * 处理订单类型切换
   */
  handleOrderTypeChange = (orderType) => {
    // 切换订单类型
    this.setState({ currentOrderType: orderType }, () => {
      // 切换后加载新类型的数据
      // this.fetchOrders() // 注释：子组件已自行管理数据获取
    })
  }

  render() {
    const { orderObj, jStoreListObj } = this.props
    const { currentOrderType } = this.state
    const { orderListPage } = orderObj
    const { fetching = false, data = {} } = orderListPage || {}
    const { records = [], totalRow = 0, pageNumber = 1, pageSize = 20 } = data
    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
        />
        <div className="i2-container">
          <Card className="i2-card-page">
            {/* 订单类型切换Tabs */}
            <Tabs
              activeKey={currentOrderType}
              onChange={this.handleOrderTypeChange}
              type="card"
              items={ORDER_TYPE_LIST.map(type => ({
                key: type.key,
                label: type.text
              }))}
            />
            
            {/* 使用工厂组件根据订单类型渲染对应的表格（包含筛选条件） */}
            <OrderTableFactory
              orderType={currentOrderType}
              loading={fetching}
              dataSource={records}
              query={this.getCurrentQuery()}
              onQueryChange={this.updateQuery}
              jStoreOptions={jStoreListObj?.data || []}
              pagination={{
                current: pageNumber,
                pageSize: pageSize,
                total: totalRow
              }}
              onPageChange={this.handlePageChange}
              onAbnormalSuccess={this.handleAbnormalSuccess}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const OrderListPageWrapper = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  
  const orderState = useSelector(state => state.order?.order || {})
  const componentsState = useSelector(state => state.components || {})
  
  const props = {
    dispatch,
    location,
    navigate,
    jStoreListObj: componentsState.getJStoreList || {},
    orderObj: orderState
  }
  
  return <OrderListPage {...props} />
}

export default OrderListPageWrapper

